import styled, { css } from 'styled-components';
import { TooltipSide, TooltipAlignment } from './DarkTooltip.types';

export const TooltipContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

interface TooltipContentProps {
  side?: TooltipSide;
  alignment?: TooltipAlignment;
}

type PositionStylesProps = { 
  side?: TooltipSide; 
  alignment?: TooltipAlignment 
};

const getPositionStyles = ({ side, alignment }: PositionStylesProps) => {
  const defaultAlignment = 'center';
  const actualSide = side || 'top';
  const actualAlignment = alignment || defaultAlignment;

  let styles = '';

  // Base positioning based on side
  switch (actualSide) {
    case 'top':
      styles += `
        bottom: calc(100% + 10px);
        left: 50%;
        transform: translateX(-50%);
      `;
      break;
    case 'bottom':
      styles += `
        top: calc(100% + 10px);
        left: 50%;
        transform: translateX(-50%);
      `;
      break;
    case 'left':
      styles += `
        right: calc(100% + 10px);
        top: 50%;
        transform: translateY(-50%);
      `;
      break;
    case 'right':
      styles += `
        left: calc(100% + 10px);
        top: 50%;
        transform: translateY(-50%);
      `;
      break;
  }

  // Adjustments based on alignment
  if (actualSide === 'top' || actualSide === 'bottom') {
    switch (actualAlignment) {
      case 'start':
        styles = styles.replace('left: 50%;', 'left: 0;');
        styles = styles.replace('transform: translateX(-50%);', 'transform: translateX(0);');
        break;
      case 'end':
        styles = styles.replace('left: 50%;', 'left: 100%;');
        styles = styles.replace('transform: translateX(-50%);', 'transform: translateX(-100%);');
        break;
    }
  } else if (actualSide === 'left' || actualSide === 'right') {
    switch (actualAlignment) {
      case 'start':
        styles = styles.replace('top: 50%;', 'top: 0;');
        styles = styles.replace('transform: translateY(-50%);', 'transform: translateY(0);');
        break;
      case 'end':
        styles = styles.replace('top: 50%;', 'top: 100%;');
        styles = styles.replace('transform: translateY(-50%);', 'transform: translateY(-100%);');
        break;
    }
  }

  return css`${styles}`;
};

const getArrowStyles = ({ side, alignment }: PositionStylesProps) => {
  const actualSide = side || 'top';
  const actualAlignment = alignment || 'center';
  
  let styles = '';
  
  switch (actualSide) {
    case 'top':
      styles += `
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border-color: #1d2939 transparent transparent transparent;
      `;
      break;
    case 'bottom':
      styles += `
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        border-color: transparent transparent #1d2939 transparent;
      `;
      break;
    case 'left':
      styles += `
        left: 100%;
        top: 50%;
        transform: translateY(-50%);
        border-color: transparent transparent transparent #1d2939;
      `;
      break;
    case 'right':
      styles += `
        right: 100%;
        top: 50%;
        transform: translateY(-50%);
        border-color: transparent #1d2939 transparent transparent;
      `;
      break;
  }

  // Adjust arrow position based on alignment
  if (actualSide === 'top' || actualSide === 'bottom') {
    switch (actualAlignment) {
      case 'start':
        styles = styles.replace('left: 50%;', 'left: 10px;');
        styles = styles.replace('transform: translateX(-50%);', '');
        break;
      case 'end':
        styles = styles.replace('left: 50%;', 'right: 10px; left: auto;');
        styles = styles.replace('transform: translateX(-50%);', '');
        break;
    }
  } else if (actualSide === 'left' || actualSide === 'right') {
    switch (actualAlignment) {
      case 'start':
        styles = styles.replace('top: 50%;', 'top: 10px;');
        styles = styles.replace('transform: translateY(-50%);', '');
        break;
      case 'end':
        styles = styles.replace('top: 50%;', 'bottom: 10px; top: auto;');
        styles = styles.replace('transform: translateY(-50%);', '');
        break;
    }
  }

  return css`${styles}`;
};

export const TooltipContent = styled.div<TooltipContentProps>`
  visibility: hidden;
  position: absolute;
  background-color: #1d2939;
  color: white;
  border-radius: 12px;
  padding: 8px 12px;
  z-index: 9999;
  
  ${({ side, alignment }) => getPositionStyles({ side, alignment })}
  
  opacity: 0;
  transition:
    opacity 0.2s ease,
    visibility 0.2s ease;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    position: absolute;
    border-width: 5px;
    border-style: solid;
    ${({ side, alignment }) => getArrowStyles({ side, alignment })}
  }

  @media (pointer: fine) {
    ${TooltipContainer}:hover & {
      visibility: visible;
      opacity: 1;
    }
  }
`;
